import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import axios from "axios"
import config from "config"
import type { AvailableCustomer, CustomerId } from "types/customer"
import useAccessToken from "./useAccessToken"

// ART-3130 multi-tenancy. GET /me/customers is the app's source of truth for
// "which customers may this account use": the memberships granted in-app
// (config.user_customer) ∪ the token's home customer ∪ every customer for
// super-admins. The persisted customer-picker selection lives alongside it
// (config.app_user.current_customer_id) — no Auth0 write, no token re-mint.
// The gateway admits any verified token to /me/*.

export type MyCustomersResponse = {
    sub: string
    is_super_admin: boolean
    home_customer_id: CustomerId | null
    current_customer_id: CustomerId | null
    customers: AvailableCustomer[]
}

export const myCustomersQueryKey = ["my-customers"]

const meUrl = `${config.railApiBasePath}/me`

export const useMyCustomers = ({ enabled = true }: { enabled?: boolean } = {}) => {
    const token = useAccessToken()

    return useQuery({
        queryKey: myCustomersQueryKey,
        queryFn: async () =>
            axios
                .get<MyCustomersResponse>(`${meUrl}/customers`, { headers: { Authorization: `Bearer ${token}` } })
                .then((res) => res.data),
        enabled: enabled && !!token,
        // Re-checked on focus so a revoke (or a new grant) reaches a live
        // session within minutes instead of at the next reload. The
        // CustomerProvider resolves the active customer once per login, so a
        // refetch only refreshes the picker — it never moves the user.
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: true,
        // one retry, then the CustomerProvider falls back to the legacy
        // (token-claim only) resolution — this call must never block login
        retry: 1,
    })
}

export const useSetCurrentCustomer = () => {
    const token = useAccessToken()
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (customerId: CustomerId) =>
            axios
                .patch<{ current_customer_id: CustomerId }>(
                    `${meUrl}/current-customer`,
                    { customerId },
                    { headers: { Authorization: `Bearer ${token}` } },
                )
                .then((res) => res.data),
        onSuccess: ({ current_customer_id }) => {
            queryClient.setQueryData<MyCustomersResponse>(myCustomersQueryKey, (previous) =>
                previous ? { ...previous, current_customer_id } : previous,
            )
        },
    })
}
